/**
 * SignalR Chat Connection
 * 
 * Chat connection adapter for .NET backend using SignalR
 */

import * as signalR from '@microsoft/signalr';
import { 
  IChatConnection, 
  ChatBackendType, 
  ChatConnectionOptions,
  ChatMessageData,
  ChatTypingData,
  ChatErrorData
} from './IChatConnection';

export class SignalRChatConnection implements IChatConnection {
  readonly type: ChatBackendType = 'dotnet';
  
  private connection: signalR.HubConnection | null = null;
  private currentSessionId: string | null = null;
  private options: ChatConnectionOptions | null = null;
  
  constructor(private url: string, private token?: string) {
    // SignalR hub URL - ExecutionHub handles both workflow execution and chat sessions
    this.url = url.endsWith('/hubs/execution') ? url : `${url}/hubs/execution`;
  }
  
  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
  
  async connect(options: ChatConnectionOptions): Promise<void> {
    try {
      this.options = options;
      this.currentSessionId = options.sessionId;
      
      // Build URL with subscriptionId query parameter (WebSocket-compatible)
      const url = options.subscriptionId
        ? `${this.url}?subscriptionId=${options.subscriptionId}`
        : this.url;
      
      // Build SignalR connection
      const connectionBuilder = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => this.token || '',
          transport: signalR.HttpTransportType.WebSockets | 
                     signalR.HttpTransportType.ServerSentEvents | 
                     signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            return delay;
          }
        })
        .configureLogging(signalR.LogLevel.Information);
      
      this.connection = connectionBuilder.build();
      
      // Connection event handlers
      this.connection.onclose((error) => {
        
        if (options.onDisconnected) {
          options.onDisconnected(error?.message || 'Connection closed');
        }
      });
      
      this.connection.onreconnecting((error) => {
      });
      
      this.connection.onreconnected((connectionId) => {
        
        // Re-subscribe to session on reconnect
        if (this.currentSessionId) {
          this.subscribeToSession(this.currentSessionId);
        }
      });
      
      // Register event listeners before connecting
      this.registerEventListeners();
      
      // Start connection
      await this.connection.start();
      
      if (options.onConnected) {
        options.onConnected();
      }
      
      // Subscribe to session after connection
      if (this.currentSessionId) {
        this.subscribeToSession(this.currentSessionId);
      }
      
    } catch (error) {
      console.error('[SignalR Chat] ❌ Failed to connect:', error);
      
      if (options.onError && error instanceof Error) {
        options.onError(error);
      }
      
      throw error;
    }
  }
  
  disconnect(): void {
    if (this.connection) {
      // Unsubscribe from session before disconnecting
      if (this.currentSessionId) {
        this.unsubscribeFromSession(this.currentSessionId);
      }
      
      this.connection.stop()
        .then(() => {})
        .catch(err => console.error('[SignalR Chat] Error disconnecting:', err));
      this.connection = null;
    }
    this.currentSessionId = null;
    this.options = null;
  }
  
  subscribeToSession(sessionId: string): void {
    
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR Chat] ⚠️ Cannot subscribe - not connected', {
        state: this.connection?.state
      });
      return;
    }
    
    this.currentSessionId = sessionId;
    
    this.connection.invoke('SubscribeToSession', sessionId)
      .then(() => {})
      .catch(err => {
        console.error('[SignalR Chat] ❌ Error subscribing to session:', err);
        console.error('[SignalR Chat] Error details:', {
          message: err.message,
          stack: err.stack,
          sessionId
        });
      });
  }
  
  unsubscribeFromSession(sessionId: string): void {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    
    this.connection.invoke('UnsubscribeFromSession', sessionId)
      .catch(err => console.error('[SignalR Chat] Error unsubscribing from session:', err));
  }
  
  sendMessage(sessionId: string, message: string): void {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR Chat] Cannot send message - not connected');
      return;
    }
    
    this.connection.invoke('SendChatMessage', sessionId, message)
      .catch(err => console.error('[SignalR Chat] Error sending message:', err));
  }
  
  getConnection(): signalR.HubConnection | null {
    return this.connection;
  }
  
  /**
   * Register all chat event listeners
   */
  private registerEventListeners(): void {
    if (!this.connection || !this.options) return;
    
    // Listen for subscription confirmation
    this.connection.on('SubscribeSessionSuccess', (data: { sessionId: string }) => {
      // Subscription confirmed
    });
    
    // Listen for chat responses
    // SignalR uses PascalCase event names
    this.connection.on('ChatResponse', (data: ChatMessageData) => {
      
      if (this.options?.onMessage) {
        this.options.onMessage(data);
      }
    });
    
    // Listen for chat errors
    this.connection.on('ChatError', (data: ChatErrorData) => {
      console.error('[SignalR Chat] Received error:', data);
      
      if (this.options?.onChatError) {
        this.options.onChatError(data);
      }
    });
    
    // Listen for typing indicators (if implemented)
    this.connection.on('ChatTyping', (data: ChatTypingData) => {
      if (this.options?.onTyping) {
        this.options.onTyping(data);
      }
    });
  }
}
